import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  Plus, 
  Globe, 
  Users, 
  Eye, 
  Edit3, 
  Copy,
  Share2,
  Settings,
  CreditCard,
  Megaphone,
  Link,
  ExternalLink,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

const PersonalPages = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [personalPages, setPersonalPages] = useState([]);
  const [stats, setStats] = useState({
    total_pages: 0,
    total_views: 0,
    total_leads: 0,
    active_pages: 0
  });

  useEffect(() => {
    loadPersonalPages();
    loadPageStats();
  }, []);

  const loadPersonalPages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/personal-pages`);
      if (response.ok) {
        const data = await response.json();
        setPersonalPages(data.pages || []);
      }
    } catch (error) {
      console.error('Error loading personal pages:', error);
      toast.error('Failed to load personal pages');
    } finally {
      setLoading(false);
    }
  };

  const loadPageStats = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.MEMBER}/personal-pages/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading page stats:', error);
    }
  };

  const handleCreatePage = (pageType) => {
    // Navigate to page builder with specific type
    toast.info(`Creating ${pageType} page...`);
    // This would navigate to the page builder component
  };

  const handleCopyLink = (pageUrl) => {
    navigator.clipboard.writeText(pageUrl);
    toast.success('Page link copied to clipboard!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPageTypeIcon = (type) => {
    switch (type) {
      case 'downline_network':
        return <Users className="h-4 w-4" />;
      case 'public_sales':
        return <CreditCard className="h-4 w-4" />;
      case 'link_in_bio':
        return <Link className="h-4 w-4" />;
      case 'lead_capture':
        return <Megaphone className="h-4 w-4" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  const getPageTypeColor = (type) => {
    switch (type) {
      case 'downline_network':
        return 'bg-blue-100 text-blue-800';
      case 'public_sales':
        return 'bg-green-100 text-green-800';
      case 'link_in_bio':
        return 'bg-purple-100 text-purple-800';
      case 'lead_capture':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Personal Pages</h1>
          <p className="text-gray-600 mt-1">
            Create and manage your personal network and sales pages
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.total_pages}</p>
                <p className="text-gray-600 text-sm">Total Pages</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.total_views}</p>
                <p className="text-gray-600 text-sm">Total Views</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.total_leads}</p>
                <p className="text-gray-600 text-sm">Total Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Settings className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.active_pages}</p>
                <p className="text-gray-600 text-sm">Active Pages</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Page Types Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="all">All Pages</TabsTrigger>
          <TabsTrigger value="downline">Downline Network</TabsTrigger>
          <TabsTrigger value="sales">Sales Pages</TabsTrigger>
          <TabsTrigger value="bio">Link-in-Bio</TabsTrigger>
          <TabsTrigger value="leads">Lead Capture</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Plus className="h-5 w-5 mr-2" />
                Create New Page
              </CardTitle>
              <CardDescription>
                Choose the type of page you want to create for your business
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Downline Network Page */}
                <Card className="hover:shadow-md transition-shadow cursor-pointer" 
                      onClick={() => handleCreatePage('downline_network')}>
                  <CardContent className="p-4 text-center">
                    <div className="p-3 bg-blue-100 rounded-lg w-fit mx-auto mb-3">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-sm mb-2">Downline Network</h3>
                    <p className="text-gray-600 text-xs">
                      Private page for your team and downline members
                    </p>
                  </CardContent>
                </Card>

                {/* Public Sales Page */}
                <Card className="hover:shadow-md transition-shadow cursor-pointer" 
                      onClick={() => handleCreatePage('public_sales')}>
                  <CardContent className="p-4 text-center">
                    <div className="p-3 bg-green-100 rounded-lg w-fit mx-auto mb-3">
                      <CreditCard className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold text-sm mb-2">Sales Page</h3>
                    <p className="text-gray-600 text-xs">
                      Public page with payment buttons and product info
                    </p>
                  </CardContent>
                </Card>

                {/* Link-in-Bio */}
                <Card className="hover:shadow-md transition-shadow cursor-pointer" 
                      onClick={() => handleCreatePage('link_in_bio')}>
                  <CardContent className="p-4 text-center">
                    <div className="p-3 bg-purple-100 rounded-lg w-fit mx-auto mb-3">
                      <Link className="h-6 w-6 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-sm mb-2">Link-in-Bio</h3>
                    <p className="text-gray-600 text-xs">
                      Social media bio page with multiple links
                    </p>
                  </CardContent>
                </Card>

                {/* Lead Capture */}
                <Card className="hover:shadow-md transition-shadow cursor-pointer" 
                      onClick={() => handleCreatePage('lead_capture')}>
                  <CardContent className="p-4 text-center">
                    <div className="p-3 bg-orange-100 rounded-lg w-fit mx-auto mb-3">
                      <Megaphone className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="font-semibold text-sm mb-2">Lead Capture</h3>
                    <p className="text-gray-600 text-xs">
                      Forms to collect leads and prospects
                    </p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Existing Pages */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Your Pages</h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading your pages...</p>
              </div>
            ) : personalPages.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No pages created yet</h3>
                  <p className="text-gray-600 mb-4">
                    Create your first personal page to start building your online presence
                  </p>
                  <Button onClick={() => handleCreatePage('link_in_bio')} className="btn-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Page
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {personalPages.map((page) => (
                  <Card key={page.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline" className={`${getPageTypeColor(page.page_type)}`}>
                              {getPageTypeIcon(page.page_type)}
                              <span className="ml-1 capitalize">
                                {page.page_type.replace('_', ' ')}
                              </span>
                            </Badge>
                            {page.is_active && (
                              <Badge variant="secondary" className="bg-green-100 text-green-800">
                                Active
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-lg">{page.title}</CardTitle>
                          <CardDescription>{page.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-4">
                        {/* Page Stats */}
                        <div className="flex justify-between text-sm text-gray-600">
                          <div className="flex items-center">
                            <Eye className="h-4 w-4 mr-1" />
                            {page.views || 0} views
                          </div>
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(page.created_at)}
                          </div>
                        </div>

                        {/* Page URL */}
                        <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                          <Input
                            value={page.page_url}
                            readOnly
                            className="text-xs bg-transparent border-none p-0 h-auto"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyLink(page.page_url)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" className="flex-1">
                            <Edit3 className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View
                          </Button>
                          <Button variant="outline" size="sm">
                            <Share2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Other tab contents would be filtered versions of the above */}
        <TabsContent value="downline">
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Downline Network Pages</h3>
            <p className="text-gray-600 mb-4">
              Create private pages for your team members and downline
            </p>
            <Button onClick={() => handleCreatePage('downline_network')} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Create Downline Page
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="sales">
          <div className="text-center py-12">
            <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sales Pages</h3>
            <p className="text-gray-600 mb-4">
              Build public sales pages with payment integration
            </p>
            <Button onClick={() => handleCreatePage('public_sales')} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Create Sales Page
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="bio">
          <div className="text-center py-12">
            <Link className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Link-in-Bio Pages</h3>
            <p className="text-gray-600 mb-4">
              Create customizable bio pages for social media
            </p>
            <Button onClick={() => handleCreatePage('link_in_bio')} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Create Bio Page
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="leads">
          <div className="text-center py-12">
            <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Lead Capture Pages</h3>
            <p className="text-gray-600 mb-4">
              Build forms to capture leads and grow your prospects
            </p>
            <Button onClick={() => handleCreatePage('lead_capture')} className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              Create Lead Page
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PersonalPages;